import { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { DIARY_GENERAL_TEXT_MAX, DIARY_ITEM_TEXT_MAX, type DiarySelectionState } from './build-diary-payload';
import { HOMEWORK_ASSIGNMENT_ID } from './templates';
import type { ClientAssignmentSelection } from './types';

const SUMMARY_MAX_LENGTH = 90;

interface RegisteredTool {
  id: string;
  title: string;
}

/** Сохранённый черновик, применяемый к состоянию вкладки. */
export type ClientAssignmentHydration = Pick<DiarySelectionState, 'includedIds' | 'tasks'>;

interface ClientAssignmentContextValue {
  tools: RegisteredTool[];
  includedIds: string[];
  tasks: Record<string, string>;
  /** Задание уже отправлено клиенту — поля редактирования блокируются. */
  isSent: boolean;
  registerTool: (tool: RegisteredTool) => void;
  unregisterTool: (id: string) => void;
  toggleInclude: (id: string) => void;
  changeTask: (id: string, task: string) => void;
  hydrate: (state: ClientAssignmentHydration) => void;
  setIsSent: (isSent: boolean) => void;
}

const ClientAssignmentContext = createContext<ClientAssignmentContextValue | null>(null);

/**
 * Состояние отправки инструментов клиенту. Секции журнала регистрируются сами,
 * поэтому список в панели повторяет то, что реально отрисовано на вкладке.
 */
export const ClientAssignmentProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [tools, setTools] = useState<RegisteredTool[]>([]);
  const [includedIds, setIncludedIds] = useState<string[]>([]);
  const [tasks, setTasks] = useState<Record<string, string>>({});
  const [isSent, setIsSent] = useState(false);
  const hydratedRef = useRef(false);

  const registerTool = useCallback((tool: RegisteredTool) => {
    setTools((prev) => (prev.some((item) => item.id === tool.id) ? prev : [...prev, tool]));
  }, []);

  const unregisterTool = useCallback((id: string) => {
    setTools((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const toggleInclude = useCallback((id: string) => {
    setIncludedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const changeTask = useCallback((id: string, task: string) => {
    setTasks((prev) => ({ ...prev, [id]: task }));
  }, []);

  // Черновик с сервера применяется один раз: повторная гидратация после рефетча
  // затёрла бы то, что психолог успел напечатать.
  const hydrate = useCallback((state: ClientAssignmentHydration) => {
    if (hydratedRef.current) return;

    hydratedRef.current = true;
    setIncludedIds(state.includedIds);
    setTasks(state.tasks);
  }, []);

  const value = useMemo(
    () => ({
      tools,
      includedIds,
      tasks,
      isSent,
      registerTool,
      unregisterTool,
      toggleInclude,
      changeTask,
      hydrate,
      setIsSent,
    }),
    [tools, includedIds, tasks, isSent, registerTool, unregisterTool, toggleInclude, changeTask, hydrate],
  );

  return <ClientAssignmentContext.Provider value={value}>{children}</ClientAssignmentContext.Provider>;
};

export interface JournalToolAssignment {
  /** Провайдер отправки активен — секция рисуется карточкой. */
  isActive: boolean;
  /** У секции есть идентификатор, её можно приложить к отправке. */
  canSend: boolean;
  isIncluded: boolean;
  task: string;
  taskLabel: string;
  /** Лимит длины текста — серверный, у домашнего задания он выше. */
  taskMaxLength: number;
  /** Задание уже отправлено клиенту — поле недоступно для правки. */
  isSent: boolean;
  onToggle: () => void;
  onTaskChange: (task: string) => void;
}

const noop = () => {};

const inactiveAssignment: JournalToolAssignment = {
  isActive: false,
  canSend: false,
  isIncluded: false,
  task: '',
  taskLabel: '',
  taskMaxLength: DIARY_ITEM_TEXT_MAX,
  isSent: false,
  onToggle: noop,
  onTaskChange: noop,
};

/** Подключает секцию журнала к отправке клиенту. Вне провайдера возвращает неактивное состояние. */
export const useToolAssignment = (id: string | undefined, title: string): JournalToolAssignment => {
  const context = useContext(ClientAssignmentContext);
  const registerTool = context?.registerTool;
  const unregisterTool = context?.unregisterTool;

  useEffect(() => {
    if (!id || !registerTool || !unregisterTool) return;

    registerTool({ id, title });
    return () => unregisterTool(id);
  }, [id, title, registerTool, unregisterTool]);

  if (!context) return inactiveAssignment;
  if (!id) return { ...inactiveAssignment, isActive: true };

  const isHomework = id === HOMEWORK_ASSIGNMENT_ID;

  return {
    isActive: true,
    canSend: true,
    isIncluded: context.includedIds.includes(id),
    task: context.tasks[id] ?? '',
    taskLabel: isHomework ? 'Текст домашнего задания' : 'Задание для клиента',
    taskMaxLength: isHomework ? DIARY_GENERAL_TEXT_MAX : DIARY_ITEM_TEXT_MAX,
    isSent: context.isSent,
    onToggle: () => context.toggleInclude(id),
    onTaskChange: (task: string) => context.changeTask(id, task),
  };
};

/** Восстановление сохранённого черновика в состояние вкладки. */
export const useClientAssignmentHydration = (): ((state: ClientAssignmentHydration) => void) => {
  const context = useContext(ClientAssignmentContext);
  return context?.hydrate ?? noop;
};

/** Отметка «задание отправлено» — выставляется хуком отправки, читается секциями инструментов. */
export const useClientAssignmentSentFlag = (): ((isSent: boolean) => void) => {
  const context = useContext(ClientAssignmentContext);
  return context?.setIsSent ?? noop;
};

const emptySelectionState: DiarySelectionState = { tools: [], includedIds: [], tasks: {} };

/** Сырое состояние выбора — для сборки тела запроса. */
export const useClientAssignmentState = (): DiarySelectionState => {
  const context = useContext(ClientAssignmentContext);
  const { tools, includedIds, tasks } = context ?? emptySelectionState;

  // Стабильная ссылка: результат попадает в зависимости useCallback у обработчиков отправки.
  return useMemo(() => ({ tools, includedIds, tasks }), [tools, includedIds, tasks]);
};

const buildSummary = (task: string): string => {
  const trimmed = task.trim();
  if (!trimmed) return 'Задание не заполнено';
  return trimmed.length > SUMMARY_MAX_LENGTH ? `${trimmed.slice(0, SUMMARY_MAX_LENGTH)}…` : trimmed;
};

/** Что уйдёт клиенту: выбранные секции в порядке их появления на вкладке. */
export const useClientAssignmentSelection = (): { items: ClientAssignmentSelection[]; totalCount: number } => {
  const context = useContext(ClientAssignmentContext);

  if (!context) return { items: [], totalCount: 0 };

  const items = context.tools
    .filter((tool) => context.includedIds.includes(tool.id))
    .map((tool) => ({
      id: tool.id,
      title: tool.title,
      summary: buildSummary(context.tasks[tool.id] ?? ''),
    }));

  return { items, totalCount: context.tools.length };
};
